import {computed, inject, Injectable, Signal, signal} from '@angular/core';
import {filter, finalize, map, Observable, of, ReplaySubject, Subject, switchMap, take} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {ApartmentDto, ApplicationDto} from "homey-housing-lottery-app-dto";
import {Application, ApplicationWithOutdatedReason, InProgressApplication,} from "../model/application";
import {ApartmentService} from "./apartment.service";
import {enforceType, joinById} from "../utils";
import {UserService} from "./user.service";
import {StorageService} from "./storage.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {environment} from "../../../environments/environment";
import {PaymentService} from "./payment/payment.service";
import {isUserEligibleForApartment} from "../model/lottery";

export type ApartmentWithApplicationInfo =
  ApartmentDto &
  ({
    readonly application: ApplicationWithOutdatedReason
  });

type ApartmentWithInProgressApplicationInfo =
  ApartmentDto &
  ({
    readonly application: InProgressApplication
  })

export const isApartmentWithInProgressApplicationInfo = (apartment: ApartmentWithApplicationInfo): apartment is ApartmentWithInProgressApplicationInfo => {
  return apartment.application?.status === 'pending-selection';
}


@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  private readonly paymentService = inject(PaymentService);
  public readonly applicationsFetching = signal(false);

  constructor(
    private readonly http: HttpClient,
    private readonly apartmentService: ApartmentService,
    private readonly userService: UserService,
    private readonly storageService: StorageService
  ) {
    this.userService.user$.subscribe(
      () => this.retrieveApplications().subscribe()
    );
  }

  get applications$(): Subject<Application[]> {
    return this._applications$;
  }

  private readonly _applications$ = new ReplaySubject<Application[]>(1);

  public retrieveApplications(): Observable<Application[]> {
    this.applicationsFetching.set(true);
    return this.userService.user$
      .pipe(
        take(1),
        filter(user => user.status === 'signup-flow' || user.status === 'mandatory-flow' || user.status === 'active'),
        switchMap(() => {
          return this.http
            .get<ApplicationDto[]>(`${environment.apiUrl}/api/private/applications`)
        }),
        map(entries => [...entries]
        ),
        map(applications => {
          this._applications$.next(applications);
          return applications;
        }),
        finalize(() => this.applicationsFetching.set(false))
      )
  }

  public readonly apartmentsWithApplicationInfo$: Observable<ApartmentWithApplicationInfo[]> = joinById(
    this.apartmentService.apartmentsSortedByPreferred$,
    this.applications$,
    "id",
    "apartmentId",
    (apartment, applicationDto) => enforceType<ApartmentWithApplicationInfo>({
      ...apartment,
      application: applicationDto
    }),
    (apartment) => ({
      ...apartment, application: undefined
    })
  );

  public readonly apartmentsWithApplicationInfo = toSignal(this.apartmentsWithApplicationInfo$);

  public readonly applyForApartment = (apartmentId: string): Observable<boolean> => {
    return this.userService.getCurrentUserAfterSignupFlow$().pipe(
      switchMap((user) => {
        if (user.paymentStatus !== 'paid') {
          if (user.paymentInfo.type === 'deferred') {
            void this.paymentService.openBenefitsDrawer();
          } else {
            this.paymentService.showNotPaidPopup();
          }
          return of(false);
        }

        return this.http.put<void>(`${environment.apiUrl}/api/public/apartments/${apartmentId}/apply`, {}).pipe(map(() => true));
      }),
      switchMap((didApply) => didApply ? this.retrieveApplications().pipe(map(() => true)) : of(false))
    )
  }

  public readonly withdrawApplication = (apartmentId: string): Observable<void> => {
    return this.userService.getCurrentUserAfterSignupFlow$().pipe(
      switchMap(() => this.http.put<void>(`${environment.apiUrl}/api/private/applications/${apartmentId}/withdraw`, {})),
      switchMap(() => this.retrieveApplications()),
      map(() => {
      })
    )
  }

  public readonly openLotteries = computed(
    () => this.apartmentsWithApplicationInfo()?.filter(apartment => apartment.status === 'open')
      .sort((a, b) => {
        if (isUserEligibleForApartment(a, this.userService.user()) || a.application) {
          return -1
        } else if (isUserEligibleForApartment(b, this.userService.user()) || b.application) {
          return 1
        } else {
          return 0
        }
      })
  );

  public readonly closedLotteries = toSignal(this.apartmentsWithApplicationInfo$.pipe(
    map(apartments => apartments.filter(apartment => apartment.status === 'closed'))
  ));

  public readonly nrReceivedApplications = computed(
    () => this.apartmentsWithApplicationInfo()?.filter((apartment) => apartment.application?.status === 'pending-selection')
  );
  public readonly selectedApplications = computed(
    () => this.apartmentsWithApplicationInfo()?.filter((apartment) => apartment.application?.status === 'selected')
  );
  public readonly notSelectedApplications = computed(
    () => this.apartmentsWithApplicationInfo()?.filter((apartment) => apartment.application?.status === 'not-selected')
  );
  public readonly submittedStageApplications = computed(
    () => this.apartmentsWithApplicationInfo()?.filter((apartment) => {
      const status = apartment.application?.status;
      return (status === 'not-synced' || status === 'applied') && apartment.status === 'open';
    })
  );
  public readonly lotteryClosedApplications = computed(
    () => this.apartmentsWithApplicationInfo()?.filter((apartment) => {
      const status = apartment.application?.status;
      return (status === 'not-synced' || status === 'applied') && apartment.status === 'closed';
    })
  );

  public readonly inProgressApplication$: Observable<ApartmentWithInProgressApplicationInfo[]> = this.apartmentsWithApplicationInfo$.pipe(
    map(entries => entries.filter(isApartmentWithInProgressApplicationInfo))
  )

  public getApartmentById = (idOrSlug: Signal<string>): Signal<ApartmentWithApplicationInfo | undefined> => computed(() => {
    const idOrSlugValue = idOrSlug();
    const apartment = this.apartmentsWithApplicationInfo()?.find(apartment => apartment.id === idOrSlugValue || apartment.slug === idOrSlugValue);

    return apartment;
  });

  public getApartmentsByIds = (ids: Signal<string[] | undefined>): Signal<ApartmentWithApplicationInfo[] | undefined> => computed(() => this.apartmentsWithApplicationInfo()?.filter(apartment => ids()?.includes(apartment.id)));

  initialize() {
    this.initializeAsync();
  }

  private async initializeAsync() {
    this.retrieveApplications().subscribe();
  }
}
